import { Button, Col, Input, Row, Space, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Permissions, SubCategory } from "../../../types/General";
import { useUpdateSubCategoryByIdMutation } from "../../../services/main";
import { showError, showToast } from "../../../helpers/toast";
import { Dispatch, SetStateAction } from "react";
import useAuth from "../../../hooks/useAuth";
import { isValidInput } from "../../../utils/validations";

const ApprovedSubCategories = ({
  subCategories,
  setApprovedSubCategories,
  page,
  searchTerm,
  modulePermissions,
  setPage,
  setSearchTerm,
  total,
}: {
  subCategories: SubCategory[];
  setApprovedSubCategories: Dispatch<SetStateAction<SubCategory[]>>;
  page: number;
  total: number;
  searchTerm: string;
  modulePermissions?: Permissions | null;
  setPage: Dispatch<SetStateAction<number>>;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}) => {
  const user = useAuth();
  const navigate = useNavigate();

  const [updateSubCategoryById] = useUpdateSubCategoryByIdMutation();

  const updateStatus = async (id: string, state: boolean) => {
    const response = await updateSubCategoryById({
      sub_category_id: id,
      body: {
        is_active: state,
      },
    }).unwrap();
    if (response?.code === 200) {
      const updatedData = subCategories.map((x) => {
        if (x.id === parseInt(id, 10)) {
          return {
            ...x,
            is_active: state,
          };
        }
        return x;
      });
      setApprovedSubCategories(updatedData);
      showToast("Status updated successfully");
    } else {
      showError("Please try again");
    }
  };

  const renderSwitch = (_: any, data: SubCategory) => {
    return (
      <Switch
        onChange={(val) => updateStatus(`${data?.id}`, val)}
        size="small"
        checked={data?.is_active}
      />
    );
  };

  const renderCategory = (_: any, data: SubCategory) => {
    return <span>{data?.category?.name}</span>;
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const columns: ColumnsType<SubCategory> = [
    {
      title: "S.no.",
      key: "sno",
      render: (value, item, index) => (page - 1) * 10 + index + 1,
      align: "center",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (_, record) => (
        <div>
          {record?.description?.length > 50
            ? `${record?.description?.slice(0, 50)}...`
            : record?.description}
        </div>
      ),
    },
    {
      title: "Category",
      key: "category",
      render: renderCategory,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Space size="middle">
          <Button
            onClick={(e) => {
              e.preventDefault();
              navigate(`/manage-subcategories/details/${record?.id}`);
            }}
          >
            <EyeOutlined style={{ color: "#52c41a" }} />
          </Button>
          {modulePermissions?.is_edit || user?.role === 1 ? (
            <Button
              onClick={(e) => {
                e.preventDefault();
                window.open(`/manage-subcategories/${record?.id}`, "_blank");
              }}
            >
              <EditOutlined color="#08c" />
            </Button>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: renderSwitch,
      className: modulePermissions?.is_edit || user?.role === 1 ? "" : "hidden",
    },
  ];

  return (
    <div>
      <div className="srch">
        <Row gutter={16}>
          <Col xs={24} md={7}>
            <Input
              value={searchTerm}
              onChange={(val) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                }
              }}
              allowClear
              placeholder="Search"
              prefix={<SearchOutlined style={{ fontSize: "20px" }} />}
              // style={{ height: 55, borderRadius: 10, minWidth: "360px" }}
              className="search-bar"
            />
          </Col>
        </Row>
        <Table
          className="table-container"
          columns={columns}
          dataSource={subCategories || []}
          pagination={{
            defaultPageSize: 10,
            current: page,
            onChange: onPageChange,
            total,
          }}
        />
      </div>
    </div>
  );
};

export default ApprovedSubCategories;
